import { BannerSeverity } from '@rover/kibble/official-patterns/Banner/BannerSeverity.constants';
import { AvailableForSelectedDatesEnum } from '@rover/rsdk/src/apiClient/latest/model/availableForSelectedDatesEnum';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import {
  getIsPriceTransparency,
  getIsPriceTransparencyCondensed,
  getIsPriceTransparencyFull,
} from '@rover/utilities/priceTransparency';

import {
  AlertContentEnum,
  getAlertContent,
  getSubHeader,
  HEADER_TITLES,
  HeaderIconEnum,
  HeaderTitlesEnum,
  SubHeadersEnum,
} from './constants';
import { BaseHeaderBuilderProps, PrebuiltHeaderProps } from './types';

interface Props extends BaseHeaderBuilderProps {
  selectedProvider: OptimizedSearchResult;
}

const buildSelectedProviderHeader = (props: Props): PrebuiltHeaderProps | object => {
  const { startDate, endDate } = props.dateData;
  const availability =
    props.selectedProvider.availableForSelectedDates || AvailableForSelectedDatesEnum.unknown;
  const isUnavailable = availability === AvailableForSelectedDatesEnum.unavailable;
  const hasDates = startDate && endDate;
  const stringProps = {
    ...props,
    sitterName: props.selectedProvider.shortName,
  };

  const isPriceTransparency = getIsPriceTransparency(props.priceTransparencyLevel);
  const isPtFull = getIsPriceTransparencyFull(props.priceTransparencyLevel);
  const isPtCondensed = getIsPriceTransparencyCondensed(props.priceTransparencyLevel);

  const title = props.i18n._(
    HEADER_TITLES[
      isUnavailable
        ? HeaderTitlesEnum.SELECTED_SITTER_UNAVAILABLE_TITLE
        : HeaderTitlesEnum.SELECTED_SITTER_TITLE
    ]
  );

  let primarySubheader: string | undefined;
  let alertText: string | undefined;

  if (!isPriceTransparency || isPtCondensed) {
    if (hasDates) {
      if (availability === AvailableForSelectedDatesEnum.available) {
        primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_AVAILABLE, stringProps);
      } else if (availability === AvailableForSelectedDatesEnum.unavailable) {
        primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_UNAVAILABLE, stringProps);
      } else {
        primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_NO_DATES, stringProps);
      }
    } else {
      primarySubheader = getSubHeader(SubHeadersEnum.SELECTED_SITTER_NO_DATES, stringProps);
    }
  } else if (!hasDates) {
    // Price transparency, no dates
    if (isPtFull) {
      alertText = getAlertContent(AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES, stringProps);
    }
  } else if (isPtFull) {
    // Price transparency, has dates
    alertText = getAlertContent(
      isUnavailable
        ? AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_UNAVAILABLE
        : AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_AVAILABLE,
      stringProps
    );
  }

  return {
    icon: HeaderIconEnum.PERSON,
    title,
    primarySubheader,
    alertText,
    alertSeverity: startDate && endDate ? BannerSeverity.INFO : BannerSeverity.WARNING,
  };
};

export default buildSelectedProviderHeader;
